import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import Logo from '../components/Logo';
import { Spinner } from '../components/Spinner';
import { evaluatePasswordStrength } from '../lib/passwordStrength';
import { supabase } from '../lib/supabase';

type Step = 1 | 2;

type Step1Errors = {
  email?: string;
  temporaryPassword?: string;
};

type Step2Errors = {
  newPassword?: string;
  confirmPassword?: string;
};

function strengthTier(password: string): 'weak' | 'medium' | 'strong' {
  if (!password) return 'weak';
  const { score } = evaluatePasswordStrength(password);
  if (score <= 1) return 'weak';
  if (score <= 3) return 'medium';
  return 'strong';
}

function strengthLabel(tier: 'weak' | 'medium' | 'strong'): string {
  if (tier === 'weak') return 'Weak';
  if (tier === 'medium') return 'Medium';
  return 'Strong';
}

export default function ResetPasswordPage() {
  const navigate = useNavigate();
  const [sessionChecked, setSessionChecked] = useState(false);
  const [step, setStep] = useState<Step>(1);

  const [email, setEmail] = useState('');
  const [temporaryPassword, setTemporaryPassword] = useState('');
  const [step1Errors, setStep1Errors] = useState<Step1Errors>({});
  const [step1FormError, setStep1FormError] = useState<string | null>(null);
  const [step1Submitting, setStep1Submitting] = useState(false);

  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [step2Errors, setStep2Errors] = useState<Step2Errors>({});
  const [passwordsMismatch, setPasswordsMismatch] = useState(false);
  const [step2FormError, setStep2FormError] = useState<string | null>(null);
  const [step2Submitting, setStep2Submitting] = useState(false);
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      const { data } = await supabase.auth.getSession();
      if (cancelled) return;
      if (data.session?.user) {
        setStep(2);
      }
      setSessionChecked(true);
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!success) return undefined;
    const t = window.setTimeout(() => {
      navigate('/dashboard', { replace: true });
    }, 2000);
    return () => window.clearTimeout(t);
  }, [success, navigate]);

  const inputClass =
    'w-full rounded-lg border border-border bg-navy px-3 py-2.5 text-sm text-foreground outline-none transition placeholder:text-muted focus:border-accent focus:ring-1 focus:ring-accent/40';

  function isValidEmail(value: string): boolean {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value.trim());
  }

  function validateStep1(): boolean {
    const next: Step1Errors = {};
    const em = email.trim();
    if (!em) next.email = 'Enter your email address.';
    else if (!isValidEmail(em)) next.email = 'Enter a valid email address.';
    if (!temporaryPassword) next.temporaryPassword = 'Enter the temporary password from your email.';
    setStep1Errors(next);
    return Object.keys(next).length === 0;
  }

  async function handleVerifyIdentity(e: React.FormEvent) {
    e.preventDefault();
    setStep1FormError(null);
    if (!validateStep1()) return;

    setStep1Submitting(true);
    const em = email.trim();
    const { error } = await supabase.auth.signInWithPassword({
      email: em,
      password: temporaryPassword,
    });

    if (error) {
      setStep1Submitting(false);
      setStep1FormError(
        'Invalid email or temporary password. Please check the email we sent you.'
      );
      return;
    }

    // Session termination: sign out all refresh tokens for this user (every device/session),
    // then sign in again here so this browser keeps a single fresh session for Step 2.
    await supabase.auth.signOut({ scope: 'global' });

    const { error: signBackError } = await supabase.auth.signInWithPassword({
      email: em,
      password: temporaryPassword,
    });
    setStep1Submitting(false);

    if (signBackError) {
      setStep1FormError(
        'Invalid email or temporary password. Please check the email we sent you.'
      );
      return;
    }

    setStep(2);
  }

  function validateStep2(): boolean {
    const next: Step2Errors = {};
    if (!newPassword) next.newPassword = 'Enter a new password.';
    else if (newPassword.length < 8) next.newPassword = 'Use at least 8 characters.';
    if (!confirmPassword) next.confirmPassword = 'Confirm your new password.';
    setStep2Errors(next);
    return Object.keys(next).length === 0;
  }

  async function handleUpdatePassword(e: React.FormEvent) {
    e.preventDefault();
    setStep2FormError(null);
    setPasswordsMismatch(false);
    if (!validateStep2()) return;

    if (newPassword !== confirmPassword) {
      setPasswordsMismatch(true);
      return;
    }

    setStep2Submitting(true);
    const { error } = await supabase.auth.updateUser({ password: newPassword });
    setStep2Submitting(false);

    if (error) {
      setStep2FormError(error.message);
      return;
    }

    setSuccess(true);
  }

  const tier = strengthTier(newPassword);

  return (
    <main className="min-h-screen bg-navy px-4 py-10 text-foreground sm:px-6 sm:py-14">
      <div className="mx-auto w-full max-w-md">
        <div className="mb-8 flex flex-col items-center text-center">
          <div className="mb-6 flex justify-center">
            <Logo size="lg" />
          </div>
          <h1 className="text-2xl font-semibold tracking-tight sm:text-3xl">Reset your password</h1>
          <p className="mt-2 text-sm text-muted sm:text-base">
            Use the temporary password from your email, then choose a new password.
          </p>
        </div>

        <div className="rounded-2xl border border-border bg-surface/80 p-6 shadow-xl backdrop-blur sm:p-8">
          <p className="mb-6 text-center text-xs font-semibold uppercase tracking-wider text-muted">
            Step {step} of 2
          </p>

          {!sessionChecked ? (
            <div className="flex flex-col items-center justify-center gap-3 py-10" aria-busy="true">
              <Spinner className="h-8 w-8 text-accent" />
              <p className="text-sm text-muted">Loading…</p>
            </div>
          ) : null}

          {sessionChecked && step === 1 ? (
            <form onSubmit={(e) => void handleVerifyIdentity(e)} noValidate>
              {step1FormError ? (
                <p
                  className="mb-4 rounded-lg border border-danger/40 bg-danger/10 px-3 py-2 text-sm text-danger"
                  role="alert"
                >
                  {step1FormError}
                </p>
              ) : null}

              <div className="space-y-4">
                <div>
                  <label htmlFor="reset-email" className="mb-1.5 block text-sm font-medium text-foreground">
                    Email address
                  </label>
                  <input
                    id="reset-email"
                    name="email"
                    type="email"
                    autoComplete="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className={`${inputClass} ${step1Errors.email ? 'border-danger' : ''}`}
                  />
                  {step1Errors.email ? (
                    <p className="mt-1.5 text-sm text-danger">{step1Errors.email}</p>
                  ) : null}
                </div>
                <div>
                  <label
                    htmlFor="reset-temp-password"
                    className="mb-1.5 block text-sm font-medium text-foreground"
                  >
                    Temporary password
                  </label>
                  <input
                    id="reset-temp-password"
                    name="temporaryPassword"
                    type="password"
                    autoComplete="current-password"
                    value={temporaryPassword}
                    onChange={(e) => setTemporaryPassword(e.target.value)}
                    className={`${inputClass} ${step1Errors.temporaryPassword ? 'border-danger' : ''}`}
                  />
                  {step1Errors.temporaryPassword ? (
                    <p className="mt-1.5 text-sm text-danger">{step1Errors.temporaryPassword}</p>
                  ) : null}
                </div>
              </div>

              <button
                type="submit"
                disabled={step1Submitting}
                className="mt-6 flex w-full min-h-[48px] items-center justify-center gap-2 rounded-lg bg-accent px-4 py-3 text-sm font-semibold text-cta-text transition hover:bg-accent/90 disabled:cursor-not-allowed disabled:opacity-70"
              >
                {step1Submitting ? (
                  <>
                    <Spinner className="h-5 w-5 text-cta-text" />
                    Verifying…
                  </>
                ) : (
                  'Verify Identity'
                )}
              </button>
            </form>
          ) : null}

          {sessionChecked && step === 2 && !success ? (
            <div>
              <p className="mb-6 text-center text-sm font-medium text-foreground">
                Identity verified. Now set your new password.
              </p>

              <form onSubmit={(e) => void handleUpdatePassword(e)} noValidate>
                {step2FormError ? (
                  <p
                    className="mb-4 rounded-lg border border-danger/40 bg-danger/10 px-3 py-2 text-sm text-danger"
                    role="alert"
                  >
                    {step2FormError}
                  </p>
                ) : null}

                <div className="space-y-4">
                  <div>
                    <label htmlFor="reset-new-password" className="mb-1.5 block text-sm font-medium text-foreground">
                      New password
                    </label>
                    <input
                      id="reset-new-password"
                      name="newPassword"
                      type="password"
                      autoComplete="new-password"
                      value={newPassword}
                      onChange={(e) => {
                        setNewPassword(e.target.value);
                        setPasswordsMismatch(false);
                      }}
                      className={`${inputClass} ${step2Errors.newPassword ? 'border-danger' : ''}`}
                    />
                    {step2Errors.newPassword ? (
                      <p className="mt-1.5 text-sm text-danger">{step2Errors.newPassword}</p>
                    ) : null}

                    {newPassword ? (
                      <div className="mt-3">
                        <div className="mb-1.5 flex items-center justify-between gap-2">
                          <span className="text-xs font-medium text-muted">Password strength</span>
                          <span
                            className={`text-xs font-semibold ${
                              tier === 'weak'
                                ? 'text-danger'
                                : tier === 'medium'
                                  ? 'text-warning'
                                  : 'text-accent'
                            }`}
                          >
                            {strengthLabel(tier)}
                          </span>
                        </div>
                        <div className="flex gap-1">
                          <span
                            className={`h-1.5 flex-1 rounded-full ${
                              tier === 'weak' ? 'bg-danger' : tier === 'medium' ? 'bg-warning' : 'bg-accent'
                            }`}
                          />
                          <span
                            className={`h-1.5 flex-1 rounded-full ${
                              tier === 'medium' ? 'bg-warning' : tier === 'strong' ? 'bg-accent' : 'bg-border'
                            }`}
                          />
                          <span
                            className={`h-1.5 flex-1 rounded-full ${
                              tier === 'strong' ? 'bg-accent' : 'bg-border'
                            }`}
                          />
                        </div>
                      </div>
                    ) : null}
                  </div>

                  <div>
                    <label
                      htmlFor="reset-confirm-password"
                      className="mb-1.5 block text-sm font-medium text-foreground"
                    >
                      Confirm new password
                    </label>
                    <input
                      id="reset-confirm-password"
                      name="confirmPassword"
                      type="password"
                      autoComplete="new-password"
                      value={confirmPassword}
                      onChange={(e) => {
                        setConfirmPassword(e.target.value);
                        setPasswordsMismatch(false);
                      }}
                      className={`${inputClass} ${step2Errors.confirmPassword || passwordsMismatch ? 'border-danger' : ''}`}
                    />
                    {step2Errors.confirmPassword ? (
                      <p className="mt-1.5 text-sm text-danger">{step2Errors.confirmPassword}</p>
                    ) : null}
                    {passwordsMismatch ? (
                      <p className="mt-1.5 text-sm text-danger">Passwords do not match</p>
                    ) : null}
                  </div>
                </div>

                <button
                  type="submit"
                  disabled={step2Submitting}
                  className="mt-6 flex w-full min-h-[48px] items-center justify-center gap-2 rounded-lg bg-accent px-4 py-3 text-sm font-semibold text-cta-text transition hover:bg-accent/90 disabled:cursor-not-allowed disabled:opacity-70"
                >
                  {step2Submitting ? (
                    <>
                      <Spinner className="h-5 w-5 text-cta-text" />
                      Updating…
                    </>
                  ) : (
                    'Update Password'
                  )}
                </button>
              </form>
            </div>
          ) : null}

          {sessionChecked && success ? (
            <p
              className="rounded-lg border border-accent/40 bg-accent/10 px-3 py-3 text-center text-sm font-medium text-accent"
              role="status"
            >
              Password updated successfully. Redirecting to your dashboard…
            </p>
          ) : null}

          <p className="mt-8 text-center text-sm text-muted">
            <Link to="/login" className="font-medium text-accent hover:underline">
              Back to log in
            </Link>
          </p>
        </div>
      </div>
    </main>
  );
}
